import React, { useCallback, useMemo, useRef, useState } from 'react';
import Tippy from '@tippyjs/react/headless';
import useMeasure from 'react-use/lib/useMeasure';
import { BaseSelect, BaseSelectProps } from '../BaseSelect/BaseSelect';
import { Control } from './components/Control';
import { useTheme } from '../../../../theme/theme';
import { Menu } from './components/Menu';
import { selectStyles } from './PopupSelect.styles';
import { Trigger } from './components/Trigger';
import { Card } from '../../Card/Card';
import { BaseOptionType } from '../BaseSelect/types';

interface PopupSelectProps<OptionType extends BaseOptionType, IsMulti extends boolean>
  extends BaseSelectProps<OptionType, IsMulti> {
  label?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  isLoading?: boolean;
  disabled?: boolean;
  inputId?: string;
  'aria-labelledby'?: string;
}

export function PopupSelect<OptionType extends BaseOptionType, IsMulti extends boolean = false>({
  value,
  label,
  isMulti,
  placeholder = 'Select...',
  searchPlaceholder = 'Search for an item...',
  variant = 'simple',
  onChange,
  disabled,
  isLoading,
  inputId = '',
  'aria-labelledby': ariaLabelledBy,
  ...rest
}: PopupSelectProps<OptionType, IsMulti>) {
  const [isOpen, setIsOpen] = useState(false);

  const [inputValue, setInputValue] = useState('');

  const triggerRef = useRef<HTMLButtonElement>(null);

  const selectInputRef = useRef<HTMLInputElement>(null);

  const [ref, { width }] = useMeasure<HTMLDivElement>();

  const theme = useTheme();

  const open = useCallback(() => {
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    // We are about to close ➜ focus the trigger
    // Hack: use a timeout to give focus on next tick
    setTimeout(() => {
      if (triggerRef.current) {
        triggerRef.current.focus();
      }
    }, 0);

    setIsOpen(false);
  }, []);

  const handleEsc = useCallback(
    e => {
      if (e.key === 'Escape') {
        close();
      }
    },
    [close],
  );

  const onInputChange = useCallback((query, { action }) => {
    // Hack: prevents resetting the input after an option has been selected
    if (action !== 'set-value') {
      setInputValue(query);
    }
  }, []);

  const triggerLabel = useMemo(() => {
    if (!isMulti) {
      if (value && !Array.isArray(value)) {
        return (value as any).label;
      }

      return placeholder;
    }

    if (label) {
      return label;
    }

    return value && Array.isArray(value) && value.length
      ? `${value.length} option${value.length > 1 ? 's' : ''} selected`
      : placeholder;
  }, [isMulti, label, placeholder, value]);

  return (
    <>
      <Tippy
        interactive
        placement="bottom-start"
        visible={isOpen}
        onClickOutside={close}
        appendTo={document.body}
        animation={false}
        onMount={() => {
          if (selectInputRef.current) {
            selectInputRef.current.focus();
          }

          document.addEventListener('keydown', handleEsc);
        }}
        onHide={() => {
          document.removeEventListener('keydown', handleEsc);
        }}
        zIndex={20000}
        offset={[0, 4]}
        render={attrs => (
          <Card
            {...attrs}
            boxShadow="500"
            width={width || 'auto'} // useMeasure is buggy in Edge/Windows (gives width: 0) ➜ fallback to auto
            border="1px solid"
            borderColor="neutral.200"
          >
            <BaseSelect
              isMulti={isMulti}
              innerRef={selectInputRef}
              value={value}
              inputValue={inputValue}
              backspaceRemovesValue={false}
              components={{
                DropdownIndicator: null,
                IndicatorSeparator: null,
                Control,
                Menu,
              }}
              controlShouldRenderValue={false}
              hideSelectedOptions={false}
              isClearable={false}
              menuIsOpen
              onChange={(val, action) => {
                if (onChange) {
                  if (isMulti) {
                    onChange(val, action);
                  } else {
                    onChange(val, action);
                    close();
                  }
                }
              }}
              placeholder={searchPlaceholder}
              styles={selectStyles(theme)}
              tabSelectsValue={false}
              variant={variant}
              closeMenuOnSelect={false}
              blurInputOnSelect={false}
              onInputChange={onInputChange}
              inputId={inputId}
              aria-labelledby={ariaLabelledBy}
              {...rest}
            />
          </Card>
        )}
      >
        <div ref={ref}>
          <Trigger
            ref={triggerRef}
            onClick={() => (isOpen ? close() : open())}
            onKeyDown={e => {
              if (e.key === 'ArrowDown') {
                open();
              }
            }}
            disabled={disabled}
            isLoading={isLoading}
            aria-labelledby={ariaLabelledBy}
          >
            {triggerLabel}
          </Trigger>
        </div>
      </Tippy>
    </>
  );
}
